import { useTheme, css } from "@emotion/react";
import React, {useEffect, useState, useRef} from "react";
import PropTypes from "prop-types";
import { useFormContext } from "@/core/providers/FormProvider";
import FieldError from "@/base/FieldError";
import { em, emu } from "@util/convertUnits";
import SVG from "react-inlinesvg";

function FormInput({
  type,
  label,
  required,
  name,
  value,
  placeholder,
  children,
  builtinError,
  boxed,
  multiple,
}) {

  const theme = useTheme();

  const { formState, setFormState } = useFormContext();

  const Select = (props) => <select {...props}>{children}</select>;
  const Component = (() => {
    if (type === "select") return Select;
    if (type === "textarea") return TextArea;
    if (type === "file") return FileInput;
    return Input;
  })()

  // HTML <input> with types "checkbox" and "radio" are edge cases
  // We implement Checkboxes using an array in order to handle multiple selection
  // Both Radio buttons and Checkboxes need a `value` prop defined
  // else they will get a value of "on" or "checked" by default
  const checked =
    (type === "radio" && formState[name] === value) ||
    (type === "checkbox" && formState[name].includes(value));

  // radio buttons and checkboxes use `checked` and `value` props
  // all other inputs use the value from formState directly
  const realValue =
    type === "checkbox" || type === "radio" ? value : formState[name];

  const containerCss = css(`
    position: relative;
    justify-content: space-between;
    width: 100%;
    margin-right: auto;
    margin-bottom: 1.8rem;
    margin-left: auto;
    font-family: ${theme.header.fontFamily};

    &.is-checkbox, &.is-radio {
      column-gap: 1.2rem;
      display: flex;
      margin-bottom: 1.2rem;
    }

    &.is-text {}
    &.is-email {}
    &.is-select {}
  `);

  const boxedContainerCss = (checked) => {
    return css(`
    border: 1px solid #979797;
    padding: 1.4rem;
    align-items: center;
    background: ${checked ? '#F4F4F4' : '#fff'};
    span{
      top: auto !important;
    }
    label{
      margin-bottom: 0;
    }
  `)
  }

  const labelCss = css(`
    display: block;
    color: #000000;
    cursor: pointer;

    .is-checkbox &, .is-radio & {
      flex-grow: 1;
      order: 1;
    }
  `);

  const inputCss = css`
    flex-shrink: 1;
    display: block;
    width: 100%;
    padding: 1.2rem 1.6rem;
    margin-bottom: ${em(8, 16)};
    font-size: ${em(16)};
    line-height: ${emu(22, 16)};
    color: #272525; // rgba(0,0,0,0.4);
    background-color: #f4f4f4;
    border: none;
    border-radius: 0.1rem;

    .is-checkbox &, .is-radio & {
      position: relative;
      top: 0.4rem;
      z-index: 1;
      flex-grow: 0;
      width: 1.865rem;
      height: 1.7rem;

      opacity: 0;
      border: none;
      
      + span {
        position: absolute;
        top: 0.4rem;
        z-index: 0;
        display: block;
        width: 1.7rem;
        height: 1.7rem;
        background-color: #f4f4f4;
      }

      &:checked + span {
          background-color: #448da4;
          background-image: url(/images/common-icons/icon-input-check.svg);
          background-position: 50% 50%;
          background-repeat: no-repeat;
        }
      
    }
    
    .is-radio & {
      + span {
        border-radius: 1.7rem;
      }
    }

    textarea& {
      min-height: 17.3rem;
    }

    select& {
      appearance: none;

      + span {
        position: absolute;
        right: 1.2rem;
        top: 5rem;
        width: 0.8em;
        height: 0.5em;
        background-color: #999999;
        clip-path: polygon(100% 0%, 0 0%, 50% 100%);
        pointer-events: none;
      }
    }

  `;

  const renderElems = () => {
    const words = <label css={labelCss} htmlFor={`${name}_${value}`}>
      {label}
      {required && <sup>*</sup>}
    </label>;

    const component = <Component
        id={`${name}_${value}`}
      type={type}
      name={name}
      // required={required}
      checked={checked}
      value={realValue}
      placeholder={placeholder}
      onChange={setFormState}
      css={inputCss}
      multiple={multiple}
    />

    const span = <span></span>;

    let err = null;
    if (builtinError) err = <FieldError name={name} />

    return <React.Fragment>{words}{component}{span}{err}</React.Fragment>

  }

  return (
    <div className={"is-"+ type} css={[containerCss, boxed ? boxedContainerCss(checked) : null]}>
      {renderElems()}
    </div>
  );
}

const Input = (props) => <input {...props} />;
const FileInput = (props) => {
  
  const [destroyKey, destroy] = useState(1);
  const [showClose, updateShowClose] = useState(false);
  const [domSafeProps, updateDomSafeProps] = useState({});
  const fileRef = useRef();

  useEffect(()=>{
    // Strip "value" from props for the file input because we're spreading 
    // this to a dom element and value is a File object for files,
    // which causes a huge error to try and assign as an attribute.
    // our current linter doesnt like using destructuring to do this so we can
    // implement a way more complex deep-copy if we want but GC should handle this fine.
    // eslint-disable-next-line no-unused-vars
    const {value, ...rest} = props 
    updateDomSafeProps(rest)
  }, [props])

  useEffect(()=>{
    if(showClose !== !!props.value){
      updateShowClose(!showClose);
    }
  }, [props.value,showClose])

  useEffect(() => {
    if(props.onChange) props.onChange({ target: fileRef.current });
    // TODO: react-hooks/exhaustive-deps dictates that "props.onChange" (or a destructured reference) 
    // should be in this dependency array, but if you do that right now, it causes a render loop, because 
    // somewhere up the chain the props.onChange method itself changes, causing it to just loop forever.
    // for now, ignoring this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destroyKey])
  
  const closeIcon = {
    position: 'absolute',
    background: 'transparent',
    border: 'none',
    top: '50%',
    transform: 'translateY(-10%)',
    right: '10px',
    svg: {
      scale: '0.5'
    }
  }

  const reset = () => {
    destroy(destroyKey+1);
  }

  return (
    <span>
      <input ref={fileRef} key={destroyKey} type={"file"} {...domSafeProps} />
      {showClose && <button css={closeIcon} type={'button'} onClick={reset}>
              <SVG src={'/images/lac-icons/lac-nav-close.svg'} cacheRequests={true} />
            </button>}
    </span>
  )
};
const TextArea = (props) => <textarea {...props}></textarea>;

FormInput.defaultProps = {
  type: "text",
  label: "",
  name: "input",
  value: "",
  required: false,
  placeholder: "",
  builtinError: false,
  boxed: false
};

FormInput.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.node,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  builtinError: PropTypes.bool,
  boxed: PropTypes.bool,
  multiple: PropTypes.object,
};

FileInput.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func
};

export default FormInput;
